import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@shared/components/ui/table";
import { Input } from "@shared/components/ui/input";
import { Button } from "@shared/components/ui/button";
import { Checkbox } from "@shared/components/ui/checkbox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@shared/components/ui/alert-dialog";
import { Badge } from "@shared/components/ui/badge";
import { Skeleton } from "@shared/components/ui/skeleton";

import { useCitiesQuery, useBulkDeleteCities } from "./useCities";
import CityActions from "./CityActions";
import { useIsMobile } from '@shared/hooks/use-mobile';
import CityCard from "./CityCard";
import CityStatistics from "./CityStatistics";
import ImportExportCities from "./ImportExportCities";
import { useTranslation } from "react-i18next";
import { useState, useMemo } from "react";
import {
  Search,
  Trash2,
  Filter,
  X,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { toast } from "sonner";

export function ListCity() {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCities, setSelectedCities] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({
    minDeliveryFees: "",
    maxDeliveryFees: "",
    isFeesFixed: "all", // "all", "yes", "no"
  });

  const limit = 20;
  const {
    data: citiesResponse,
    isLoading,
    error,
  } = useCitiesQuery(searchTerm, limit, currentPage);
  const { mutate: bulkDeleteCities, isPending: isBulkDeleting } =
    useBulkDeleteCities();
  const isMobile = useIsMobile();

  // Extract cities and pagination info from response
  const totalCount = citiesResponse?.totalCount || 0;
  const totalPages = citiesResponse?.totalPages || 1;
  const hasNextPage = citiesResponse?.hasNextPage || false;
  const hasPrevPage = citiesResponse?.hasPrevPage || false;

  // Filter cities based on client-side filters only (search is handled server-side)
  const filteredCities = useMemo(() => {
    const cities = citiesResponse?.cities || [];
    if (!cities) return [];

    return cities.filter((city) => {
      // Delivery fees filter
      const deliveryFees = parseFloat(city.deliveryFees);
      const matchesMinFees =
        filters.minDeliveryFees === "" ||
        deliveryFees >= parseFloat(filters.minDeliveryFees);
      const matchesMaxFees =
        filters.maxDeliveryFees === "" ||
        deliveryFees <= parseFloat(filters.maxDeliveryFees);

      // Fixed fees filter
      const matchesFixedFees =
        filters.isFeesFixed === "all" ||
        (filters.isFeesFixed === "yes" && city.isFeesFixed) ||
        (filters.isFeesFixed === "no" && !city.isFeesFixed);

      return matchesMinFees && matchesMaxFees && matchesFixedFees;
    });
  }, [citiesResponse, filters]);

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedCities(filteredCities.map((city) => city._id));
    } else {
      setSelectedCities([]);
    }
  };

  const handleSelectCity = (cityId, checked) => {
    if (checked) {
      setSelectedCities([...selectedCities, cityId]);
    } else {
      setSelectedCities(selectedCities.filter((id) => id !== cityId));
    }
  };

  const handleBulkDelete = () => {
    bulkDeleteCities(selectedCities, {
      onSuccess: () => {
        toast.success(
          t("messages.citiesBulkDeleted", { count: selectedCities.length })
        );
        setSelectedCities([]);
      },
      onError: (error) => {
        toast.error(error.message || t("messages.citiesBulkDeleteFailed"));
      },
    });
  };

  const clearFilters = () => {
    setFilters({
      minDeliveryFees: "",
      maxDeliveryFees: "",
      isFeesFixed: "all",
    });
    setSearchTerm("");
    setCurrentPage(1); // Reset to first page when clearing filters
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    setSelectedCities([]); // Clear selections when changing pages
  };

  const handleSearchChange = (value) => {
    setSearchTerm(value);
    setCurrentPage(1); // Reset to first page when searching
  };

  const hasActiveFilters =
    searchTerm ||
    filters.minDeliveryFees ||
    filters.maxDeliveryFees ||
    filters.isFeesFixed !== "all";
  const isAllSelected =
    filteredCities.length > 0 &&
    selectedCities.length === filteredCities.length;
  const isSomeSelected =
    selectedCities.length > 0 && selectedCities.length < filteredCities.length;

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-center">
        <div className="text-red-600 mb-2">
          {t("messages.errorLoadingCities")}
        </div>
        <Button onClick={() => window.location.reload()} variant="outline">
          {t("common.retry")}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Statistics */}
      <CityStatistics />

      <div className="space-y-4">
        {/* Header Actions */}
        <div className="flex flex-col gap-4">
          <div className="flex flex-col sm:flex-row gap-2 justify-between items-start sm:items-center">
            <div className="flex flex-col sm:flex-row gap-2 flex-1">
              <CityActions />

              {selectedCities.length > 0 && (
                <div className="flex gap-2">
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:text-red-700"
                        disabled={isBulkDeleting}
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        {t("city.deleteSelected")} ({selectedCities.length})
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>
                          {t("city.bulkDeleteConfirmTitle")}
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                          {t("city.bulkDeleteConfirmMessage", {
                            count: selectedCities.length,
                          })}
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>
                          {t("common.cancel")}
                        </AlertDialogCancel>
                        <AlertDialogAction
                          onClick={handleBulkDelete}
                          disabled={isBulkDeleting}
                          className="bg-red-600 hover:bg-red-700"
                        >
                          {isBulkDeleting
                            ? t("common.deleting")
                            : t("common.delete")}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              )}
            </div>

            <div className="flex gap-2">
              <ImportExportCities cities={filteredCities} />

              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filter className="h-4 w-4 mr-1" />
                {t("common.filters")}
                {hasActiveFilters && (
                  <Badge
                    variant="destructive"
                    className="ml-1 px-1 py-0 text-xs"
                  >
                    !
                  </Badge>
                )}
              </Button>
            </div>
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute ltr:left-3 rtl:right-4 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input
              placeholder={t("city.searchPlaceholder")}
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="pl-10 pr-10"
            />
            {searchTerm && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleSearchChange("")}
                className="absolute right-1 top-1/2 transform -translate-y-1/2 h-8 w-8 p-0"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>

          {/* Filters */}
          {showFilters && (
            <div className="border rounded-lg p-4 bg-gray-50 dark:bg-gray-800">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="text-sm font-medium mb-1 block">
                    {t("city.filters.minDeliveryFees")}
                  </label>
                  <Input
                    type="number"
                    min="0"
                    placeholder="0"
                    value={filters.minDeliveryFees}
                    onChange={(e) =>
                      setFilters({
                        ...filters,
                        minDeliveryFees: e.target.value,
                      })
                    }
                  />
                </div>

                <div>
                  <label className="text-sm font-medium mb-1 block">
                    {t("city.filters.maxDeliveryFees")}
                  </label>
                  <Input
                    type="number"
                    min="0"
                    placeholder="1000"
                    value={filters.maxDeliveryFees}
                    onChange={(e) =>
                      setFilters({
                        ...filters,
                        maxDeliveryFees: e.target.value,
                      })
                    }
                  />
                </div>

                <div>
                  <label className="text-sm font-medium mb-1 block">
                    {t("common.fees.fixedFees")}
                  </label>
                  <select
                    value={filters.isFeesFixed}
                    onChange={(e) =>
                      setFilters({ ...filters, isFeesFixed: e.target.value })
                    }
                    className="w-full h-10 px-3 py-2 border border-input bg-background rounded-md text-sm"
                  >
                    <option value="all">{t("common.all")}</option>
                    <option value="yes">{t("common.yes")}</option>
                    <option value="no">{t("common.no")}</option>
                  </select>
                </div>

                <div className="flex items-end">
                  <Button
                    variant="outline"
                    onClick={clearFilters}
                    disabled={!hasActiveFilters}
                    className="w-full"
                  >
                    <X className="h-4 w-4 mr-1" />
                    {t("common.clearFilters")}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Results Summary */}
          <div className="flex justify-between items-center text-sm text-gray-600 dark:text-gray-400">
            <span>
              {totalCount > 0 && (
                <>
                  {filteredCities.length} {t("city.resultsCount")}{" "}
                  {t("common.of")} {totalCount}
                  {searchTerm && ` ${t("common.for")} "${searchTerm}"`}
                </>
              )}
            </span>
            {selectedCities.length > 0 && (
              <span>
                {selectedCities.length} {t("common.selected")}
              </span>
            )}
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600 dark:text-gray-400">
                {t("common.page")} {currentPage} {t("common.of")} {totalPages}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={!hasPrevPage}
                >
                  <ChevronLeft className="h-4 w-4" />
                  {t("common.previous")}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={!hasNextPage}
                >
                  {t("common.next")}
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Content */}
        {isLoading ? (
          <div className="space-y-4">
            {isMobile ? (
              Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="border rounded-lg p-4">
                  <Skeleton className="h-4 w-1/3 mb-2" />
                  <Skeleton className="h-4 w-1/2 mb-2" />
                  <Skeleton className="h-4 w-1/4" />
                </div>
              ))
            ) : (
              <div className="border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12"></TableHead>
                      <TableHead>{t("city.name")}</TableHead>
                      <TableHead>{t("city.arabicName")}</TableHead>
                      <TableHead>{t("common.deadline")}</TableHead>
                      <TableHead>{t("common.fees.deliveryFees")}</TableHead>
                      <TableHead>{t("common.fees.cancellationFees")}</TableHead>
                      <TableHead>{t("common.fees.refusalFees")}</TableHead>
                      <TableHead>{t("common.fees.fixedFees")}</TableHead>
                      <TableHead className="text-center">
                        {t("action.actions")}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {Array.from({ length: 5 }).map((_, i) => (
                      <TableRow key={i}>
                        <TableCell>
                          <Skeleton className="h-4 w-4" />
                        </TableCell>
                        <TableCell>
                          <Skeleton className="h-4 w-20" />
                        </TableCell>
                        <TableCell>
                          <Skeleton className="h-4 w-20" />
                        </TableCell>
                        <TableCell>
                          <Skeleton className="h-4 w-16" />
                        </TableCell>
                        <TableCell>
                          <Skeleton className="h-4 w-12" />
                        </TableCell>
                        <TableCell>
                          <Skeleton className="h-4 w-12" />
                        </TableCell>
                        <TableCell>
                          <Skeleton className="h-4 w-12" />
                        </TableCell>
                        <TableCell>
                          <Skeleton className="h-4 w-12" />
                        </TableCell>
                        <TableCell>
                          <Skeleton className="h-8 w-16" />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        ) : filteredCities.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-gray-500 mb-2">
              {searchTerm || hasActiveFilters
                ? t("city.noResultsFound")
                : t("city.noCitiesYet")}
            </div>
            {searchTerm || hasActiveFilters ? (
              <Button variant="outline" onClick={clearFilters}>
                {t("common.clearFilters")}
              </Button>
            ) : (
              <CityActions />
            )}
          </div>
        ) : isMobile ? (
          <div className="flex flex-col gap-2">
            {filteredCities.map((city) => (
              <div key={city._id} className="flex items-center gap-2">
                <Checkbox
                  checked={selectedCities.includes(city._id)}
                  onCheckedChange={(checked) =>
                    handleSelectCity(city._id, checked)
                  }
                />
                <div className="flex-1">
                  <CityCard city={city} />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">
                    <Checkbox
                      checked={isAllSelected}
                      ref={(el) => el && (el.indeterminate = isSomeSelected)}
                      onCheckedChange={handleSelectAll}
                    />
                  </TableHead>
                  <TableHead>{t("city.name")}</TableHead>
                  <TableHead>{t("city.arabicName")}</TableHead>
                  <TableHead>{t("common.deadline")}</TableHead>
                  <TableHead>{t("common.fees.deliveryFees")}</TableHead>
                  <TableHead>{t("common.fees.cancellationFees")}</TableHead>
                  <TableHead>{t("common.fees.refusalFees")}</TableHead>
                  <TableHead>{t("common.fees.fixedFees")}</TableHead>
                  <TableHead className="text-center">
                    {t("action.actions")}
                  </TableHead>
                </TableRow>
              </TableHeader>

              <TableBody>
                {filteredCities.map((city) => (
                  <TableRow
                    key={city._id}
                    className={
                      selectedCities.includes(city._id)
                        ? "bg-blue-50 dark:bg-blue-900/20"
                        : ""
                    }
                  >
                    <TableCell>
                      <Checkbox
                        checked={selectedCities.includes(city._id)}
                        onCheckedChange={(checked) =>
                          handleSelectCity(city._id, checked)
                        }
                      />
                    </TableCell>
                    <TableCell className="font-medium">{city.name}</TableCell>
                    <TableCell className="font-medium">
                      {city.nameArabic}
                    </TableCell>
                    <TableCell className="font-medium">
                      {city.deadline}
                    </TableCell>
                    <TableCell className="font-medium">
                      {city.deliveryFees} {t("main.currency")}
                    </TableCell>
                    <TableCell className="font-medium">
                      {city.cancellationFees} {t("main.currency")}
                    </TableCell>
                    <TableCell className="font-medium">
                      {city.refusalFees} {t("main.currency")}
                    </TableCell>
                    <TableCell className="font-medium">
                      <Badge
                        variant={city.isFeesFixed ? "default" : "secondary"}
                      >
                        {city.isFeesFixed ? t("common.yes") : t("common.no")}
                      </Badge>
                    </TableCell>

                    <TableCell className="flex gap-2 items-center justify-center">
                      <CityActions mode="update" initialData={city} />
                      <CityActions mode="delete" initialData={city} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}
